<script setup lang="ts">
/**
 * 新機能ガイド用コンポーネント。
 *
 * クエリパラメータで対象 DOM を `NitMessageBox`(main) で囲って強調し、
 * 自前の吹き出しで説明を表示する。説明末尾の OK ボタンで閉じる。
 *   - `guide_target`: 対象の CSS セレクタ（必須）
 *   - `guide_title`:  タイトル（任意, 50字でカット）
 *   - `guide_desc`:   説明文（任意, 200字でカット）
 *   - `guide_pos`:    `top` / `bottom` / `left` / `right`（既定 `bottom`）
 *
 * 例: ?guide_target=%23settings-button&guide_title=新機能&guide_desc=ここから設定
 */
import NitMessageBox from '@/atoms/NitMessageBox.vue'
import { deleteQuery } from '@/helpers/hasQuery'
import { computed, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

const QUERY_KEYS = [
  'guide_target',
  'guide_title',
  'guide_desc',
  'guide_pos'
] as const

const TITLE_MAX = 50
const DESC_MAX = 200
const HIGHLIGHT_PADDING = 4
const TOOLTIP_DISTANCE = 12
const VIEWPORT_MARGIN = 8

type Placement = 'top' | 'bottom' | 'left' | 'right'
const PLACEMENTS: Placement[] = ['top', 'bottom', 'left', 'right']

const route = useRoute()
const targetEl = ref<HTMLElement | null>(null)
const targetRect = ref<DOMRect | null>(null)
const tooltipEl = ref<HTMLElement | null>(null)
const tooltipSize = ref<{ width: number; height: number }>({
  width: 0,
  height: 0
})

const getQueryString = (key: string): string | null => {
  const v = route.query[key]
  return typeof v === 'string' && v.length > 0 ? v : null
}

const targetSelector = computed(() => getQueryString('guide_target'))
const title = computed(() => getQueryString('guide_title')?.slice(0, TITLE_MAX))
const description = computed(() =>
  getQueryString('guide_desc')?.slice(0, DESC_MAX)
)
const placement = computed<Placement>(() => {
  const raw = getQueryString('guide_pos')
  return PLACEMENTS.includes(raw as Placement) ? (raw as Placement) : 'bottom'
})

const isActive = computed(
  () => !!targetSelector.value && (!!title.value || !!description.value)
)

let rafId: number | null = null
let observer: MutationObserver | null = null

const findAndUpdate = () => {
  const sel = targetSelector.value
  if (!sel) {
    targetEl.value = null
    targetRect.value = null
    return
  }
  if (!targetEl.value || !document.contains(targetEl.value)) {
    try {
      targetEl.value = document.querySelector<HTMLElement>(sel)
    } catch {
      targetEl.value = null
    }
  }
  if (targetEl.value) {
    const rect = targetEl.value.getBoundingClientRect()
    targetRect.value = rect.width === 0 && rect.height === 0 ? null : rect
  } else {
    targetRect.value = null
  }
  if (tooltipEl.value) {
    const tRect = tooltipEl.value.getBoundingClientRect()
    if (
      tRect.width !== tooltipSize.value.width ||
      tRect.height !== tooltipSize.value.height
    ) {
      tooltipSize.value = { width: tRect.width, height: tRect.height }
    }
  }
}

const tick = () => {
  findAndUpdate()
  rafId = requestAnimationFrame(tick)
}

const startWatching = () => {
  stopWatching()
  findAndUpdate()
  rafId = requestAnimationFrame(tick)
  observer = new MutationObserver(() => findAndUpdate())
  observer.observe(document.body, { childList: true, subtree: true })
}

const stopWatching = () => {
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
  observer?.disconnect()
  observer = null
  targetEl.value = null
  targetRect.value = null
  tooltipSize.value = { width: 0, height: 0 }
}

watch(isActive, (a) => (a ? startWatching() : stopWatching()), {
  immediate: true
})
onUnmounted(() => stopWatching())

const close = () => {
  QUERY_KEYS.forEach((k) => deleteQuery(k))
  stopWatching()
}

const highlightStyle = computed(() => {
  const r = targetRect.value
  if (!r) return null
  return {
    top: `${r.top - HIGHLIGHT_PADDING}px`,
    left: `${r.left - HIGHLIGHT_PADDING}px`,
    width: `${r.width + HIGHLIGHT_PADDING * 2}px`,
    height: `${r.height + HIGHLIGHT_PADDING * 2}px`
  }
})

const tooltipStyle = computed(() => {
  const r = targetRect.value
  const { width: tw, height: th } = tooltipSize.value
  // サイズ未取得時は不可視で位置だけ確定させ、初回フレームでの位置飛びを防ぐ
  if (!r || tw === 0 || th === 0) {
    return { visibility: 'hidden' as const, top: '0px', left: '0px' }
  }
  let top = 0
  let left = 0
  switch (placement.value) {
    case 'top':
      top = r.top - HIGHLIGHT_PADDING - th - TOOLTIP_DISTANCE
      left = r.left + r.width / 2 - tw / 2
      break
    case 'bottom':
      top = r.bottom + HIGHLIGHT_PADDING + TOOLTIP_DISTANCE
      left = r.left + r.width / 2 - tw / 2
      break
    case 'left':
      top = r.top + r.height / 2 - th / 2
      left = r.left - HIGHLIGHT_PADDING - tw - TOOLTIP_DISTANCE
      break
    case 'right':
      top = r.top + r.height / 2 - th / 2
      left = r.right + HIGHLIGHT_PADDING + TOOLTIP_DISTANCE
      break
  }
  const maxLeft = window.innerWidth - tw - VIEWPORT_MARGIN
  const maxTop = window.innerHeight - th - VIEWPORT_MARGIN
  left = Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft))
  top = Math.max(VIEWPORT_MARGIN, Math.min(top, maxTop))
  return {
    top: `${top}px`,
    left: `${left}px`
  }
})
</script>

<template>
  <template v-if="isActive && targetRect">
    <NitMessageBox
      type="main"
      class="feature-guide-highlight bd-8px"
      :style="highlightStyle"
    />
    <Teleport to="body">
      <div
        ref="tooltipEl"
        class="feature-guide-tooltip"
        :class="`placement-${placement}`"
        :style="tooltipStyle"
      >
        <div v-if="title" class="feature-guide-tt-title">{{ title }}</div>
        <div v-if="description" class="feature-guide-tt-desc">
          {{ description }}
        </div>
        <div class="feature-guide-tt-actions">
          <button type="button" class="feature-guide-tt-ok" @click="close">
            OK
          </button>
        </div>
      </div>
    </Teleport>
  </template>
</template>

<style scoped>
.feature-guide-highlight {
  position: fixed;
  pointer-events: none;
  z-index: var(--z-index-90);
  display: block;
  box-sizing: border-box;
  border: 2px solid var(--main);
  animation:
    feature-guide-pulse 1.6s ease-out infinite,
    feature-guide-blink 1.6s ease-in-out infinite;
}
.feature-guide-highlight.main {
  background: rgba(66, 159, 159, 0);
}
@keyframes feature-guide-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(66, 159, 159, 0.65);
  }
  70% {
    box-shadow: 0 0 0 14px rgba(66, 159, 159, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(66, 159, 159, 0);
  }
}
@keyframes feature-guide-blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
}
@media (prefers-reduced-motion: reduce) {
  .feature-guide-highlight {
    animation: none;
  }
}
</style>
